import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatClock } from '../../constants';

interface Props {
  testTitle: string;
  sectionTitle: string;
  /** "Section 2 of 3". */
  position: string;
  /** Seconds left, or null when the section is untimed. */
  remaining: number | null;
  lowTime: boolean;
  timerHidden: boolean;
  onToggleTimer: () => void;
  saving: boolean;
  versionCode?: string;
  /** Who is sitting the test, under the test title. */
  candidateName?: string;
  /** Reading settings and full screen, already styled for the dark bar. */
  tools: React.ReactNode;
  actionLabel: string;
  actionTitle: string;
  onAction: () => void;
  busy: boolean;
}

/** The navy bar of the computer-delivered IELTS test: test and section on the left, the clock and tools on the right. */
export function IeltsTopBar({
  testTitle,
  sectionTitle,
  position,
  remaining,
  lowTime,
  timerHidden,
  onToggleTimer,
  saving,
  versionCode,
  candidateName,
  tools,
  actionLabel,
  actionTitle,
  onAction,
  busy,
}: Props) {
  const { t } = useTranslation();
  const showClock = remaining !== null && (!timerHidden || lowTime);

  return (
    <>
      <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 bg-[#0A1628] px-4 py-2.5 text-white sm:h-16 sm:flex-nowrap sm:gap-x-5 sm:px-6 sm:py-0">
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:min-w-[220px] sm:flex-none lg:min-w-[300px]">
          <span
            className="hidden h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[#D1FE17] font-display text-base font-extrabold text-[#10182B] sm:flex"
            aria-hidden
          >
            E
          </span>
          <div className="min-w-0 leading-tight">
            <h1 className="truncate font-display text-[15px] font-extrabold sm:hidden">{sectionTitle}</h1>
            <p className="truncate text-sm font-bold max-sm:hidden">{testTitle}</p>
            <p className="truncate text-xs text-slate-400">
              <span className="sm:hidden">{position}</span>
              <span className="hidden sm:inline">
                {[candidateName, versionCode ? t('exam.version_code', { code: versionCode }) : null].filter(Boolean).join(' · ') || position}
              </span>
            </p>
          </div>
        </div>

        <div className="hidden min-w-0 flex-1 text-center leading-tight sm:block">
          <p className="truncate font-display text-[19px] font-extrabold tracking-[-0.02em]">{sectionTitle}</p>
          <p className="truncate text-[12.5px] text-slate-400">{position}</p>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <span className="mr-1 hidden items-center gap-1.5 text-xs text-slate-400 xl:inline-flex" aria-hidden>
            <span className={cn('h-[7px] w-[7px] rounded-full', saving ? 'bg-slate-500' : 'bg-green-500')} />
            {saving ? t('exam.saving') : t('saved')}
          </span>
          {tools}
          {remaining !== null && (
            <>
              {showClock ? (
                <span
                  role="timer"
                  aria-label={t('exam.time_left', { time: formatClock(remaining) })}
                  className={cn(
                    'flex items-baseline gap-2 rounded-[10px] px-3 py-1.5 font-mono text-lg font-bold tabular-nums sm:text-[22px]',
                    lowTime ? 'bg-[#DC2626] text-white' : 'bg-white/[0.08]',
                  )}
                >
                  {formatClock(remaining)}
                  <small className={cn('font-sans text-[11.5px] font-semibold max-sm:hidden', lowTime ? 'text-red-100' : 'text-slate-400')}>
                    {t('exam.review.time_left')}
                  </small>
                </span>
              ) : (
                <span className="px-2 text-xs text-slate-400">{t('exam.timer_hidden')}</span>
              )}
              <button
                type="button"
                onClick={onToggleTimer}
                aria-pressed={timerHidden}
                aria-label={timerHidden ? t('exam.show_timer') : t('exam.hide_timer')}
                title={timerHidden ? t('exam.show_timer') : t('exam.hide_timer')}
                className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] text-slate-300 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#D1FE17]"
              >
                {timerHidden ? <Eye className="h-5 w-5" aria-hidden /> : <EyeOff className="h-5 w-5" aria-hidden />}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onAction}
            disabled={busy}
            title={actionTitle}
            className="ml-1.5 inline-flex h-[38px] min-w-0 max-w-[16rem] items-center gap-2 rounded-[11px] bg-[#D1FE17] px-4 text-[13.5px] font-bold text-[#10182B] transition hover:brightness-95 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#D1FE17] disabled:opacity-50"
          >
            <span className="truncate">{actionLabel}</span>
          </button>
        </div>
      </header>

      {lowTime && (
        <p role="alert" className="shrink-0 bg-[#FDECEC] px-4 py-2 text-center text-[13px] font-semibold text-[#7A1A12] sm:px-6">
          {t('exam.low_time')}
        </p>
      )}
    </>
  );
}

export default IeltsTopBar;
